import { Grid } from '@material-ui/core';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  FormControlLabel,
  Icons,
  MaterialPalette,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { emailForTaskBulkSignAndStamp, saveSignAndStamp } from '../../graphql';
import { emailForAdhocTaskBulkSignAndStamp, emailForScAfterLiSign } from '../../graphql/Email';
import { saveBulkSignAndStamp } from '../../graphql/SignAndStamp';
import { useMst } from '../../mobx-models/Root';
import { IInspectionTask, ISignAndStampData, ISignAndStampFileBulk } from '../../models/models';
import { InspectionList as InspectionListType } from '../../rxdb/inspectionList/queryBuilder';
import { SignAndStampFile } from '../../rxdb/signAndStamp/queryBuilder';
import useBulkSignAndStamp from '../../rxdb/signAndStamp/useBulkSignAndStamp';
import { useSignAndStampFile } from '../../rxdb/signAndStamp/useSignAndStampFile';
import { SignAndStampUserLevelDocument } from '../../rxdb/signAndStampUserLevel/queryBuilder';
import { useSignAndStampUserLevel } from '../../rxdb/signAndStampUserLevel/useSignAndStampUserLevel';
import { TaskList } from '../../rxdb/taskList/queryBuilder';
import { useRxDB } from '../../rxdb/useRxDB';
import { isJestTest } from '../../test-helpers/isJestTest';
import { sentryCaptureException, useFileStorage, useNotification } from '../../utilities';
import {
  ALLOW_EXTENSIONS,
  FALSE,
  INVALID_FILE,
  INVALID_FILE_TYPE,
  SPECIAL_CHARACTER_ERROR,
  TRUE,
} from '../../utilities/constants';
import { BatchSize, FileCategory, InspectionStatus, TaskStatus } from '../../utilities/enums';
import {
  checkFileCorruption,
  getBulkSignAndStampPayload,
  getLocalStorageCurrentUserId,
  getValueFromLocalStorage,
  hasSpecialCharacter,
} from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton';
import signAndStampStyles from '../SignAndStamp/SignAndStamp.styles';
import SignAndStampFIView from '../SignAndStampFIView';
import UserConsent from '../UserSignatureAndStamp/UserConsent';

interface Props {
  open: boolean;
  onClose: () => void;
  taskList: TaskList[];
  inspectionList: InspectionListType[];
}
const BulkSignAndStamp: React.FC<Props> = ({ open, onClose, taskList, inspectionList }) => {
  const classes = signAndStampStyles();
  const { HandleGesture, primaryContext, CurrentUserSelected, SiteDiscipline } = useMst();
  const { db } = useRxDB();

  const signatureCanvasHeight = '500';
  let signatureCanvas: SignatureCanvas | null;
  const {
    uploadSignAndStamp,
    bulkUploadToS3,
    uploadFilesInCache,
    getAttachmentFromIndexDB,
    getBlobForBulkSignAndStamp,
    saveBlobForBulkSignAndStamp,
  } = useFileStorage();

  const [isSignature, setIsSignature] = useState<boolean>(false);
  const [confirmationForSignAndStamp, setConfirmationForSignAndStamp] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<ISignAndStampData[]>([]);
  const [userSignData, setUserSignData] = useState<ISignAndStampData>();
  const [userStampData, setUserStampData] = useState<ISignAndStampData>();
  const [signData, setSignData] = useState<ISignAndStampData>();
  const [stampData, setStampData] = useState<ISignAndStampData>();
  const [disableCheckedCheckbox, setDisableCheckedCheckbox] = useState<boolean>(false);
  const [isUploadButDisable, setIsUploadButDisable] = useState<boolean>(true);
  const [isSaveCheckDisable, setIsSaveCheckDisable] = useState<boolean>(true);
  const [isSaveForFuture, setIsSaveForFuture] = useState<boolean>(false);
  const [deleteUserFiles, setDeleteUserFiles] = useState<ISignAndStampData[]>([]);
  const { successNotification, errorNotification } = useNotification();
  const [openUserConsent, setOpenUserConsent] = useState<boolean>(false);
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [openAlert, setIsOpenAlert] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUserConsentEnable, setIsUserConsentEnable] = useState<boolean>(false);
  const { deleteSignAndStampFile } = useSignAndStampFile();
  const localStorageCurrentUserId = getLocalStorageCurrentUserId();
  const {
    signAndStampUserLevelInitialized,
    signAndStampUserLevelCollection,
    getSignAndStampUserLevel,
  } = useSignAndStampUserLevel();
  const [signAndStampUserLevelUpdateSubscription, setSignAndStampUserLevelUpdateSubscription] =
    useState<Subscription>();
  const { signAndStampDataFromInspections } = useBulkSignAndStamp(
    HandleGesture?.tasksforReAssignAndSign?.toJSON()?.map(task => String(task?.inspectionId)) || [],
  );
  const label = (
    <Grid item container>
      <Typography color="textPrimary" variant="body1" className={classes.content}>
        Please review
      </Typography>
      {!isUserConsentEnable && (
        <Typography color="textSecondary" className={classes.spanMsgDisable}>
          User Consent
        </Typography>
      )}
      {isUserConsentEnable && (
        <Typography
          color="primary"
          className={classes.spanMsg}
          onClick={() => setOpenUserConsent(true)}
          data-testid="user-consent-text"
        >
          User Consent
        </Typography>
      )}
      <Typography color="textPrimary" variant="body1" className={classes.content}>
        for saving Signature and Stamp for future use.
      </Typography>
    </Grid>
  );

  const checkTasksUnderInspections = (inspectionId: number, taskToExclude: number[]) => {
    return (
      taskList?.find(
        e =>
          e.fkAssetInspection === inspectionId &&
          e.fkInspectionTaskStatusCategory !== TaskStatus.LI_SIGN_PENDING &&
          !taskToExclude.includes(e.pkAssetInspectionTask),
      ) ||
      inspectionList?.find(
        (v: InspectionListType) =>
          v.inspectionId === String(inspectionId) &&
          v.assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING,
      )
    );
  };

  const getSignAndStampUserLevelFromRxDB = async () => {
    if (signAndStampUserLevelCollection) {
      const signAndStampUserLevelRxDocument: SignAndStampUserLevelDocument[] =
        await getSignAndStampUserLevel();
      if (signAndStampUserLevelRxDocument) {
        const signUserData = signAndStampUserLevelRxDocument.find(
          (item: SignAndStampUserLevelDocument) =>
            item.fileCategory === FileCategory.SIGN &&
            item.userId === localStorageCurrentUserId &&
            item.isSaveForFuture === true &&
            item.isDelete === FALSE,
        );
        if (signUserData) {
          const data: ISignAndStampData = {
            fileUUID: signUserData.fileUUID,
            updatedName: signUserData.name,
            category: signUserData.fileCategory,
            filePath: signUserData.originalPath,
          };
          setUserSignData(data);
        }
        const stampUserData = signAndStampUserLevelRxDocument.find(
          (item: SignAndStampUserLevelDocument) =>
            item.fileCategory === FileCategory.STAMP &&
            item.userId === localStorageCurrentUserId &&
            item.isSaveForFuture === true &&
            item.isDelete === FALSE,
        );
        if (stampUserData) {
          const data: ISignAndStampData = {
            fileUUID: stampUserData.fileUUID,
            updatedName: stampUserData.name,
            category: stampUserData.fileCategory,
            filePath: stampUserData.originalPath,
          };
          setUserStampData(data);
        }
      }
    }
  };

  const subscribeToSignAndStampUserLevel = async () => {
    if (signAndStampUserLevelInitialized && signAndStampUserLevelCollection) {
      const signAndStampUserLevelUpdateSub = signAndStampUserLevelCollection
        .find()
        .$.subscribe(() => {
          getSignAndStampUserLevelFromRxDB();
        });
      setSignAndStampUserLevelUpdateSubscription(signAndStampUserLevelUpdateSub);
    }
  };

  const onOpenUploadForSign = async () => {
    document.getElementById('selectImageForSign')?.click();
  };

  const onOpenUploadForStamp = async () => {
    document.getElementById('selectImageForStamp')?.click();
  };

  const onFilesSelected = async (images: ISignAndStampData[]) => {
    await setSelectedFiles(images);
  };

  const autoVersionIfFileExists = (name: string, index = 0): string => {
    let newName = name;
    let ext = '';
    if (index) {
      if (newName.indexOf('.') > -1) {
        const tokens = newName.split('.');
        ext = `.${tokens.pop()}`;
        newName = tokens.join('.');
      }
      newName = `${newName} (${index})${ext}`;
    }
    const nameExists = selectedFiles.filter(f => f.updatedName === newName).length > 0;
    return nameExists ? autoVersionIfFileExists(name, index + 1) : newName;
  };

  const uploadStampImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file: File = e.target.files[0];
    if (!ALLOW_EXTENSIONS.exec(file.name)) {
      errorNotification(INVALID_FILE_TYPE);
      return;
    }
    if (hasSpecialCharacter(file.name)) {
      errorNotification(SPECIAL_CHARACTER_ERROR);
      return;
    }
    let validItem = null;
    validItem = await checkFileCorruption([file]);
    if (!validItem.length) {
      errorNotification(INVALID_FILE);
      return;
    }
    if (file) {
      const fileData = {
        fileData: file,
        updatedName: autoVersionIfFileExists(file.name),
        category: FileCategory.STAMP,
        isSignature: false,
      };
      setDisableCheckedCheckbox(false);
      setStampData(fileData);
      setSelectedFiles(arr => [...arr, ...[fileData]]);
    }
  };

  const uploadSignImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file: File = e.target.files[0];
    if (!ALLOW_EXTENSIONS.exec(file.name)) {
      errorNotification(INVALID_FILE_TYPE);
      return;
    }
    if (hasSpecialCharacter(file.name)) {
      errorNotification(SPECIAL_CHARACTER_ERROR);
      return;
    }
    let validItem = null;
    validItem = await checkFileCorruption([file]);
    if (!validItem.length) {
      errorNotification(INVALID_FILE);
      return;
    }
    if (file) {
      const fileData = {
        fileData: file,
        updatedName: autoVersionIfFileExists(file.name),
        category: FileCategory.SIGN,
        isSignature: false,
      };
      setDisableCheckedCheckbox(false);
      setSignData(fileData);
      setSelectedFiles(arr => [...arr, ...[fileData]]);
    }
  };

  const onsetDeleteUserFiles = async (data: ISignAndStampData[]) => {
    setDeleteUserFiles(data);
  };

  const handleFileDelete = async (fileId: string, fileCategory: number) => {
    switch (fileCategory) {
      case 11:
      case 12:
        await deleteSignAndStampFile(String(fileId), TRUE);
        onsetDeleteUserFiles(deleteUserFiles.filter(image => image.fileUUID !== fileId));
        break;
    }
  };

  const getSignature = async () => {
    const val = signatureCanvas?.toDataURL();
    const blob = await fetch(val as string).then(r => r.blob());
    const objectURL = URL.createObjectURL(blob);
    const fileName = 'sign.png';
    const file = new File([objectURL], `${fileName}`, { type: 'image/png' });
    const fileData = {
      fileData: file,
      updatedName: autoVersionIfFileExists(file.name),
      category: FileCategory.SIGN,
      isSignature: true,
      isSaveForFuture,
      blob,
    };
    return fileData;
  };

  const getInspectionAndTaskIdsMapping = () => {
    const tempInspectionTask: IInspectionTask[] = HandleGesture.tasksforReAssignAndSign
      .toJSON()
      .map(task => {
        if (
          task.taskStatusId === TaskStatus.FI_SIGN_PENDING &&
          task.inspectionStatusId === InspectionStatus.FI_SIGN_PENDING
        )
          return {
            inspectionId: task.inspectionId,
            taskId: task.taskId,
          };
        else if (
          task.taskStatusId === TaskStatus.LI_SIGN_PENDING &&
          task.inspectionStatusId === InspectionStatus.LI_SIGN_PENDING
        )
          return {
            inspectionId: task.inspectionId,
            taskId: 0,
            isLiFiScSame: task.isLiFiScSame,
          };
        return {
          inspectionId: -1,
          taskId: -1,
        };
      })
      ?.filter(task => task.inspectionId !== -1 && task.taskId !== -1);

    const inspectionTask = tempInspectionTask.filter(
      (value, index, array) =>
        !!(
          array.findIndex(
            e => e.inspectionId === value.inspectionId && e.taskId === value.taskId,
          ) === index
        ),
    );

    return inspectionTask;
  };

  const handleImageUploads = async (fileList: ISignAndStampData[]) => {
    const files = [...fileList];
    files.forEach((file, index) => {
      files[index].isSaveForFuture = isSaveForFuture;
      files[index].isInspection = true;
    });
    if (isSaveForFuture && deleteUserFiles) {
      files.forEach((file, index) => {
        deleteUserFiles.forEach((dFile, dIndex) => {
          if (files[index].updatedName === deleteUserFiles[dIndex].updatedName) {
            files[index].isSaveForFuture = false;
          }
        });
      });
    }

    // save files for future use on user level
    for (const file in files) {
      try {
        if (files[file]?.isSaveForFuture) {
          const fileData: File = files[file].fileData as File;
          const data = files[file];
          const signAndStampObj: ISignAndStampFileBulk = (await uploadSignAndStamp(
            `signAndStamp/${localStorageCurrentUserId}`,
            fileData,
            data,
          )) as ISignAndStampFileBulk;

          delete signAndStampObj?.dateCreated;
          delete signAndStampObj?.dateModified;
          const apiResponse = await saveSignAndStamp(signAndStampObj);

          await bulkUploadToS3(
            signAndStampObj?.originalPath,
            files[file].fileData as File,
            files[file],
          );
          if (apiResponse.errors) {
            throw new Error(`Error in sign or stamp for user level`);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error for user level sign and stamp', error);
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(
          error,
          '~ file: BulkSignAndStamp.tsx ~ line 349 ~ handleImageUploads ~ failed while saving on user level',
          'bulkOperation',
          JSON.stringify(files),
        );
      }
    }
    // save files for sign and stamp
    try {
      const inspectionIds = HandleGesture.inspectionIds.toJSON();
      const taskIds = HandleGesture.taskIds.toJSON();
      if (HandleGesture.tasksforReAssignAndSign.length) {
        const inspectionTask = getInspectionAndTaskIdsMapping();

        const liSignToSC = inspectionTask.filter(item => item.taskId === 0 && !item.isLiFiScSame);
        let selectedTaskWithFiles: ISignAndStampData[] = [];
        let listOfInspections: { inspectionId: number }[] = [];

        const filesWithBlob: ISignAndStampData[] = [];
        await Promise.all(
          files.map(async file => {
            let blob = file?.blob || null;
            if (!(file.category == FileCategory.SIGN && file.isSignature))
              blob = await getBlobForBulkSignAndStamp(file);

            if (blob) filesWithBlob.push({ ...file, blob });
          }),
        );
        filesWithBlob.forEach(file => {
          inspectionTask.forEach(item => {
            selectedTaskWithFiles = [...selectedTaskWithFiles, { ...file, ...item }];
          });
        });

        inspectionIds.map(inspectionId => {
          const taskListLength = checkTasksUnderInspections(inspectionId, taskIds);
          if (!taskListLength) {
            listOfInspections = [...listOfInspections, { inspectionId }];
          }
        });

        let position = 0;
        const batchSize = 5;
        let tempResult: SignAndStampFile[] = [];
        while (position < selectedTaskWithFiles.length) {
          const itemsInBatch = selectedTaskWithFiles.slice(position, position + batchSize);
          tempResult = [
            ...tempResult,
            ...(await Promise.all(itemsInBatch.map(saveBlobForBulkSignAndStamp))),
          ];
          position += batchSize;
        }

        const bulkFilesList: ISignAndStampFileBulk[] = [];
        tempResult?.forEach(file => {
          if (file)
            bulkFilesList.push({
              ...file,
              isBulkOperation: true,
              isLiStamp: file?.fkAssetInspectionTask === 0,
            });
        });

        const variables = getBulkSignAndStampPayload(bulkFilesList);
        position = 0;
        let results: void[] = [];

        while (position < variables.length) {
          const itemsInBatch = variables.slice(position, position + BatchSize.SIGN_AND_STAMP);
          results = [
            ...results,
            ...(await Promise.all(
              itemsInBatch.map(async variable => {
                try {
                  const apiResponse = await saveBulkSignAndStamp(variable);
                  if (apiResponse.errors) {
                    console.error(apiResponse.errors);
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error(error);
                  // console logs will be removed once sentry implementation is done
                  sentryCaptureException(error, `Error in sign or stamp for ${variable}`);
                }
              }),
            )),
          ];
          position += BatchSize.SIGN_AND_STAMP;
        }

        const runOnTask =
          CurrentUserSelected.isAdhoc ||
          inspectionTask.find(
            item =>
              item.taskId !== 0 &&
              listOfInspections.find(insp => insp.inspectionId === item.inspectionId) === undefined,
          );
        if (results.length === variables.length) {
          if (listOfInspections.length || liSignToSC.length) {
            HandleGesture.setRunOnInspectionSubscription(true);
          } else {
            if (runOnTask) {
              HandleGesture.setRunOnTaskSubscription(true);
            }
          }
          if (listOfInspections.length) {
            try {
              if (CurrentUserSelected.isAdhoc) {
                const temp = HandleGesture.tasksforReAssignAndSign
                  .toJSON()
                  .filter(
                    task =>
                      task.leadInspectorId !== task.fieldInspectorId &&
                      !signAndStampDataFromInspections.find(
                        sign => sign.inspectionId === String(task.inspectionId),
                      ),
                  )
                  .map(task => {
                    return {
                      inspectionId: task.inspectionId,
                      taskId: task.taskId,
                      previousFI: task.fieldInspectorId,
                      leadInspectorId: task.leadInspectorId,
                      isActionByFI: true,
                    };
                  });
                if (temp?.length > 0) {
                  await emailForAdhocTaskBulkSignAndStamp(SiteDiscipline.siteId, temp);
                }
              } else {
                await emailForTaskBulkSignAndStamp(
                  CurrentUserSelected.workPackageId,
                  Number(getValueFromLocalStorage('inspectorId')), //not needed
                  taskList[0].fkInspectorLead,
                  listOfInspections,
                  true,
                );
              }
            } catch (error) {
              sentryCaptureException(
                error,
                '~ file: BulkSignAndStamp.tsx ~ line 403 ~ emailForTaskBulkSignAndStamp ~',
              );
            }
          }
          if (!CurrentUserSelected.isAdhoc && liSignToSC.length > 0) {
            try {
              await emailForScAfterLiSign(
                CurrentUserSelected.workPackageId,
                taskList[0].fkInspectorLead,
                liSignToSC.map(item => {
                  return { inspectionId: item.inspectionId };
                }),
              );
            } catch (error) {
              sentryCaptureException(
                error,
                '~ file: BulkSignAndStamp.tsx ~ line 514 ~ emailForScAfterLiSign ~',
              );
            }
          }
          if (
            inspectionTask.find(item => item.taskId === 0 && item.isLiFiScSame) ||
            CurrentUserSelected.isAdhoc
          ) {
            if (!CurrentUserSelected.isAdhoc) HandleGesture.setRunOnDeletion(true);
            try {
              primaryContext.getWorkPackageFromMobx(
                SiteDiscipline.siteId,
                SiteDiscipline.disciplineId,
              );
              primaryContext.getInspectionListFromMobx(
                SiteDiscipline.siteId,
                SiteDiscipline.disciplineId,
              );
            } catch (error) {
              sentryCaptureException(error, '~ file: BulkSignAndStamp.tsx ~ line 446 ~');
            }
          }
          setIsLoading(false);
          setConfirmationForSignAndStamp(false);
          HandleGesture.resetEverything();
          onClose();
          HandleGesture.setIsCompleted(true);
          try {
            db?.startReplication();
          } catch (error) {
            sentryCaptureException(
              error,
              '~ file: BulkSignAndStamp.tsx ~ line 459 ~ startReplication',
            );
            HandleGesture.setIsCompleted(false);
          }
          setTimeout(() => {
            successNotification('Sign and Stamp Submitted Successfully');
            uploadFilesInCache();
          }, 100);
        } else {
          sentryCaptureException(
            new Error('Upload File Error'),
            `~ file: BulkSignAndStamp.tsx ~ line 469 ~ result: ${results?.length} == selectedTaskWithFiles: ${selectedTaskWithFiles?.length}`,
          );
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '🚀 ~ file: BulkSignAndStamp.tsx ~ line 450 ~ handleImageUploads ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: BulkSignAndStamp.tsx ~ line 450 ~ handleImageUploads ~',
      );
    }
  };

  const handleDelete = (file: ISignAndStampData) => {
    if (file.fileUUID) {
      if (userSignData && userSignData?.fileUUID === file.fileUUID) {
        setUserSignData(undefined);
      }
      if (userStampData && userStampData?.fileUUID === file.fileUUID) {
        setUserStampData(undefined);
      }
    } else if (file.category === FileCategory.SIGN) {
      setSignData(undefined);
    } else if (file.category === FileCategory.STAMP) {
      setStampData(undefined);
    }
    if (deleteUserFiles) {
      deleteUserFiles.map(image => {
        if (image.fileUUID && file.fileUUID && image.fileUUID === file.fileUUID) {
          handleFileDelete(image.fileUUID, image.category);
        }
      });
    }
    onFilesSelected(selectedFiles.filter(image => image.updatedName !== file.updatedName));
  };

  const handleSignAndStamp = async () => {
    setIsLoading(true);
    if (isSignature) {
      const fileData = await getSignature();
      selectedFiles.push(fileData);
    }
    await handleImageUploads(selectedFiles);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked === false) {
      setIsAccepted(false);
    }
    if (e.target.checked === true) {
      setIsAccepted(false);
    }
    if (isAccepted) {
      setIsChecked(e.target.checked);
      setIsOpenAlert(false);
    } else if (e.target.checked === true) {
      if (isAccepted) setIsOpenAlert(false);
      else setIsOpenAlert(true);
    } else if (!isAccepted) {
      setIsOpenAlert(true);
    }
  };

  useEffect(() => {
    if (signAndStampUserLevelInitialized) {
      getSignAndStampUserLevelFromRxDB();
      subscribeToSignAndStampUserLevel();
    }
    return () => {
      if (signAndStampUserLevelUpdateSubscription)
        signAndStampUserLevelUpdateSubscription.unsubscribe();
    };
  }, [signAndStampUserLevelInitialized]);

  useEffect(() => {
    setIsSaveForFuture(isChecked);
  }, [isChecked]);

  const editImageFile = async (dataURL: string, fileName: string) => {
    const blobUrl = await (await fetch(dataURL)).blob();
    const file = new File([blobUrl], `${fileName}`, { type: 'image/jpeg' });
    return file;
  };

  const getS3URL = async (fileId: string) => {
    const blobFile: Blob = (await getAttachmentFromIndexDB(fileId)) as Blob;
    const url = blobFile ? URL.createObjectURL(blobFile) : '';
    return url;
  };

  const setUrlToFile = async (data: ISignAndStampData) => {
    const url = await getS3URL(data.fileUUID as string);
    if (url !== '') {
      const file = await editImageFile(url, data.updatedName);
      const fileData: ISignAndStampData = {
        fileUUID: data.fileUUID,
        fileData: file as File,
        updatedName: data.updatedName,
        category: data.category,
        isSignature: false,
        isSaveForFuture: false,
      };
      setSelectedFiles(prevArr => {
        const filteredArr = prevArr.filter(item => item.fileUUID !== fileData.fileUUID);
        return [...filteredArr, ...[fileData]];
      });
      setDeleteUserFiles(prevArr => {
        const filteredArr = prevArr.filter(item => item.fileUUID !== fileData.fileUUID);
        return [...filteredArr, ...[fileData]];
      });
    }
  };

  useEffect(() => {
    if (userSignData || userStampData) {
      if (userSignData && userSignData.fileUUID) {
        setUrlToFile(userSignData);
      }
      if (userStampData && userStampData.fileUUID) {
        setUrlToFile(userStampData);
      }
    }
  }, [userSignData, userStampData]);

  useEffect(() => {
    //user consent hyperlink enable/disable
    if (signData || isSignature || stampData) {
      setIsUserConsentEnable(true);
      setDisableCheckedCheckbox(false);
    } else if (!signData && !isSignature && !stampData) {
      setIsUserConsentEnable(false);
    }
  }, [isSignature, signData, stampData]);

  useEffect(() => {
    //upload button
    if (signData || isSignature || userSignData) {
      setIsUploadButDisable(false);
    } else if (!signData && !isSignature && !userSignData) {
      setIsUploadButDisable(true);
    }
  }, [isSignature, signData, stampData, userSignData]);

  useEffect(() => {
    //user consent checkbox enbale/disable
    if (signData || isSignature || stampData) {
      setIsSaveCheckDisable(false);
    } else if (!signData && !isSignature && !stampData) {
      setIsSaveCheckDisable(true);
    }
  }, [isSignature, signData, stampData]);

  useEffect(() => {
    //consent checkbox check
    if (!signData && !isSignature && !stampData && isChecked) {
      setIsChecked(false);
    }
  }, [isSignature, signData, stampData]);

  useEffect(() => {
    setIsSaveForFuture(isChecked);
  }, [isChecked]);

  useEffect(() => {
    if (!isSignature && userSignData) {
      setIsUploadButDisable(false);
      setDisableCheckedCheckbox(true);
    }
  }, [isSignature, userSignData, userStampData]);

  useEffect(() => {
    if (HandleGesture.isLIstampingRequired && !userStampData) {
      if (!stampData) {
        setIsUploadButDisable(true);
      }
    }
  }, [
    signData,
    isSignature,
    stampData,
    userStampData,
    HandleGesture.isLIstampingRequired,
    userSignData,
  ]);

  useEffect(() => {
    if (!signData && !stampData && !isSignature && !userSignData && !userStampData) {
      setIsUploadButDisable(true);
      setIsUserConsentEnable(false);
      setIsChecked(false);
    }
  }, [signData, stampData, isSignature, userSignData, userStampData]);

  return (
    <>
      {
        <Dialog
          data-testid="bulk-sign-and-stamp-dialog"
          id={uuidv4()}
          fullScreen
          open={open}
          titleText="Sign & Stamp"
          backButton={false}
          onClose={onClose}
          dialogActions={
            <Grid
              container
              justifyContent="space-between"
              spacing={2}
              className={classes.dialogActionGrid}
            >
              <Grid item>
                <Button onClick={onClose} variant="outlined" color="primary" noMinHeight>
                  Cancel
                </Button>
              </Grid>
              <CustomButton
                disabled={isUploadButDisable}
                onClose={() => {
                  setConfirmationForSignAndStamp(true);
                  setIsUploadButDisable(true);
                  setIsSaveCheckDisable(true);
                }}
                data-testid="sign-and-stamp-FI-upload"
                buttonText="Confirm & Send"
              />
            </Grid>
          }
          maxWidth="lg"
        >
          <Card>
            <Grid xs={12} container className={classes.signStampContainer}>
              <Grid xs={6} className={classes.signStampTitle}>
                <Typography variant="h5" />
              </Grid>
              <Grid item xs={12} className={classes.signatureContainer}>
                {userSignData && (
                  <SignAndStampFIView
                    data={userSignData}
                    onDelete={handleDelete}
                    readonly={false}
                  />
                )}
                {signData && (
                  <SignAndStampFIView data={signData} onDelete={handleDelete} readonly={false} />
                )}
                {!userSignData && !signData && (
                  <>
                    {isSignature && (
                      <Grid
                        item
                        container
                        xs={12}
                        justifyContent="flex-end"
                        className={classes.signStampTitle1}
                      >
                        <Typography
                          variant="h5"
                          color="error"
                          className="clearSignText"
                          onClick={() => {
                            signatureCanvas?.clear();
                            setIsSignature(false);
                          }}
                        >
                          Clear Sign
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      {!isJestTest() ? (
                        <SignatureCanvas
                          ref={ref => {
                            signatureCanvas = ref;
                          }}
                          penColor={`${MaterialPalette.text.disabled}`}
                          backgroundColor="#404040"
                          canvasProps={{
                            height: signatureCanvasHeight,
                            className: classes.signCanvas,
                            style: { maxWidth: '100%', borderRadius: '4px' },
                          }}
                          onBegin={() => {}}
                          onEnd={() => setIsSignature(true)}
                        />
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </>
                )}
                {!isSignature && !userSignData && !signData && (
                  <Grid item container justifyContent="center" className={classes.signLabelDiv}>
                    <Typography className={classes.signLabel}>
                      {`Digitally sign above to complete this inspection `}
                    </Typography>
                    <Typography className={classes.signLabel}>{`OR `}</Typography>
                    <Grid item className={classes.signUploadButDiv}>
                      <Button
                        color="primary"
                        className={classes.signUploadButton}
                        noMinHeight
                        onClick={onOpenUploadForSign}
                      >
                        <Grid className={classes.uploadButtonSignGrid}>
                          <Typography variant="subtitle2" className="uploadButtonsign">
                            Upload Signature*
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              id="selectImageForSign"
                              onClick={() => {}}
                              onChange={uploadSignImage}
                              data-testid="upload-sign-file"
                            />
                          </Typography>
                        </Grid>
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} className={classes.stampContainer}>
                {!userStampData && !stampData && (
                  <Grid container className={classes.stampUploadContainer}>
                    <Grid container justifyContent="center">
                      <Grid className={classes.stampLabelDiv}>
                        <Typography>
                          Select and Upload the Stamp Image
                          {HandleGesture.isLIstampingRequired ? '*' : ''}
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        item
                        onClick={onOpenUploadForStamp}
                        className={classes.uploadIconGrid}
                      >
                        <Icons.UploadFeather color="primary" height="40px" width="40px" />
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          id="selectImageForStamp"
                          onClick={() => {}}
                          onChange={uploadStampImage}
                          data-testid="upload-stamp-file"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {userStampData && (
                  <SignAndStampFIView
                    data={userStampData}
                    onDelete={handleDelete}
                    readonly={false}
                  />
                )}
                {stampData && (
                  <SignAndStampFIView data={stampData} onDelete={handleDelete} readonly={false} />
                )}
              </Grid>
              <Grid
                item
                xs={12}
                container
                justifyContent="flex-end"
                alignItems="center"
                className={classes.accordianRightDiv}
              >
                <Grid item className={classes.floatLeftGrid}>
                  <Grid container className={classes.checkboxGrid}>
                    {disableCheckedCheckbox ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={true}
                            color="primary"
                            checked={true}
                            className={classes.checkBoxUserConsent}
                          />
                        }
                        label={label}
                      />
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isSaveCheckDisable}
                            color="primary"
                            checked={isChecked}
                            onChange={e => handleChange(e)}
                            className={classes.checkBoxUserConsent}
                            inputProps={{ 'data-testid': 'save-for-future-checkbox' }}
                          />
                        }
                        label={label}
                      />
                    )}
                  </Grid>
                  {openAlert && (
                    <Grid className={classes.errorGrid}>
                      <Icons.AlertCircleIcon color="error" />
                      <Typography color="error" variant="body2">
                        Complete reading User Consent
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {confirmationForSignAndStamp && (
              <Dialog
                onClose={() => {
                  if (!isLoading) {
                    setConfirmationForSignAndStamp(false);
                    setIsSaveCheckDisable(true);
                    setIsUploadButDisable(false);
                  }
                }}
                open={confirmationForSignAndStamp}
                titleText="Confirmation"
                backButton={false}
                disableEnforceFocus={isLoading ? true : undefined}
                BackdropProps={{
                  onClick: event => {
                    if (isLoading) event.preventDefault();
                  },
                  onTouchStart: event => {
                    if (isLoading) event.preventDefault();
                  },
                  style: { touchAction: isLoading ? 'none' : 'auto' },
                }}
                dialogActions={
                  <Grid container justifyContent="space-between" spacing={2}>
                    <Grid item>
                      <Button
                        data-testid="cancel-sign-and-stamp-upload"
                        variant="outlined"
                        color="primary"
                        noMinHeight
                        onClick={() => {
                          setConfirmationForSignAndStamp(false);
                          setIsSaveCheckDisable(true);
                          setIsUploadButDisable(false);
                        }}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item className={classes.confirmButtonDiv}>
                      <Button
                        variant="contained"
                        color="primary"
                        noMinHeight
                        isLoading={isLoading}
                        onClick={() => {
                          if (!isLoading) {
                            handleSignAndStamp();
                            setSelectedFiles([]);
                          }
                        }}
                        data-testid="save-sign-and-stamp-upload"
                      >
                        Yes, Send
                      </Button>
                    </Grid>
                  </Grid>
                }
                maxWidth="xs"
                noMinHeight
              >
                <Grid container className={classes.confirmationContainer}>
                  <Grid item container xs={12} alignItems="center">
                    <Typography className={classes.dialogText}>
                      {`Once you upload and send the Signature and Stamping you can't make any further changes.
                  Are you sure to send the Signature & Stamping?
                `}
                    </Typography>
                  </Grid>
                </Grid>
              </Dialog>
            )}
          </Card>
        </Dialog>
      }
      {openUserConsent && (
        <UserConsent
          open={openUserConsent}
          onClose={() => setOpenUserConsent(false)}
          setIsAccepted={setIsAccepted}
          setIsChecked={setIsChecked}
          setIsOpenAlert={setIsOpenAlert}
        />
      )}
    </>
  );
};

export default observer(BulkSignAndStamp);
